import React from 'react'
import { useTranslation } from 'react-i18next'

import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Popover from '@mui/material/Popover'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings'
import EditIcon from '@mui/icons-material/Edit'

import { msToTime } from '../../utils/dateUtils'
import { convertToTempScale, getFullscreenElement, getLogEvents, getValueSuffix } from '../../utils/graphUtils'
import { GraphDatapoint, GraphSeriesConfig } from '../../types/graphSeriesConfig'
import { BasicDataParam, CalculatedDataParam, GraphDataParam } from '../../types/enums/graphDataParam'
import { LogEvent } from '../../types/logEvent'
import { EventTypeString } from '../../types/enums/eventType'
import { SeriesType } from '../../types/enums/seriesType'
import TooltipIconButton from '../fields/tooltipIconButton'
import { getLiveViewTempSeries, setLiveViewTempSeries } from '../../utils/localStorageUtils'
import ProfileLink from '../logs/profileLink'
import { Log } from '../../types/log'
import { LAST_EVENT_SERIES, TempViewSeries } from '../../types/tempViewSeries'
import { weightValueToDisplay } from '../../utils/weightUtils'
import InventoryLink from '../logs/inventoryLink'

interface TempViewProps {
  events: Array<LogEvent>
  status: string
  profile: string
  profileId: number
  logName: string
  start_weight: number
  end_weight: number
  inventoryId: number
  beanName: string
  machineSlug: string
  seriesConfig: Array<GraphSeriesConfig>
  compareSeriesConfig: Array<GraphSeriesConfig>
  onSetName?: () => void
  onSetWeight?: () => void
  onSetBean?: () => void
}

const TempView = (props: TempViewProps): JSX.Element => {
  const { t } = useTranslation()

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const [enabledParams, setEnabledParams] = React.useState<Array<TempViewSeries>>(getLiveViewTempSeries())

  React.useEffect(() => {
    // update localStorage when temp series array is changed
    setLiveViewTempSeries(enabledParams)
  }, [enabledParams])

  const renderTempEl = (
    label: string,
    value: string,
    largeText: boolean,
    borderColor?: string,
    compareElement?: JSX.Element
  ): JSX.Element => {
    return (
      <Stack>
        <Typography variant="body2">{label}</Typography>
        <Typography
          sx={{
            alignSelf: 'flex-start',
            typography: { xs: largeText ? 'h1' : 'h2', md: largeText ? 'h3' : 'h4' },
            borderBottom: borderColor ? `3px solid ${borderColor}` : undefined,
          }}
        >
          {value}
        </Typography>
        {compareElement}
      </Stack>
    )
  }

  const renderCompareEl = (compareBatch: string, compareValue: string, compareDiff: number): JSX.Element => {
    return (
      <Stack direction="row" sx={{ fontSize: 12 }}>
        <Box>
          {compareBatch + ': '}
          <Box fontWeight="medium" display="inline">
            {compareValue}
          </Box>
        </Box>
        <Box sx={{ marginLeft: 1 }}>
          {'Δ: '}
          <Box fontWeight="medium" display="inline">
            {(compareDiff > 0 ? '+' : '') + compareDiff.toFixed(1)}
          </Box>
        </Box>
      </Stack>
    )
  }

  const getLastValue = (param: GraphDataParam): GraphDatapoint | null => {
    const series = props.seriesConfig.find((x) => x.param === param)
    return series && series.datapoints.length > 0 ? series.datapoints[series.datapoints.length - 1] : null
  }

  // Find the datapoint at msec or the last datapoint in the series if msec is past the end of the series
  const getSeriesValue = (param: GraphDataParam, msec: number, series: GraphSeriesConfig): GraphDatapoint | null => {
    const datapoint = series?.datapoints[msec / 1000]
    if (datapoint?.msec === msec) {
      return datapoint
    }
    const seriesLength = series?.datapoints.length || 0
    return series?.datapoints.find((point) => point.msec >= msec) || series?.datapoints[seriesLength - 1] || null
  }

  const getLastEvent = (): string => {
    const btPoints = props.seriesConfig.find((x) => x.param === BasicDataParam.BT)?.datapoints || []
    const sortedEvents = getLogEvents(props.events, null, '', btPoints).events.sort((a, b) => a.msec - b.msec)
    const lastEvent =
      sortedEvents.length > 0 && sortedEvents[sortedEvents.length - 1].type !== null
        ? sortedEvents[sortedEvents.length - 1].type
        : null
    return lastEvent ? t(EventTypeString[lastEvent]) : '-'
  }

  return (
    <Stack spacing={3} sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
      <Popover
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        container={getFullscreenElement() || document.body}
        onClose={() => setMenuAnchorEl(null)}
      >
        <Stack sx={{ p: 2 }}>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">{t('liveview.showseries')}</FormLabel>
            <FormGroup sx={{ alignItems: 'flex-start' }}>
              {[...props.seriesConfig, { param: LAST_EVENT_SERIES, seriesLabel: t('liveview.lastevent') }]
                .filter((x) => x.param !== BasicDataParam.TARGET && x.param !== BasicDataParam.CRACK)
                .map((x) => {
                  return (
                    <FormControlLabel
                      key={x.param}
                      control={
                        <Checkbox
                          checked={enabledParams.indexOf(x.param) > -1}
                          onChange={(_event, isChecked) => {
                            setEnabledParams((oldParams) => {
                              return isChecked ? [...oldParams, x.param] : oldParams.filter((y) => y !== x.param)
                            })
                          }}
                        />
                      }
                      label={t(x.seriesLabel)}
                    />
                  )
                })}
            </FormGroup>
          </FormControl>
        </Stack>
      </Popover>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="flex-start">
          <Typography variant="body2">{props.logName || '-'}</Typography>
          <Stack sx={{ mt: '-10px', height: '10px' }}>
            {props.onSetName && (
              <TooltipIconButton onClick={props.onSetName} tooltip={t('logview.editlogname')} icon={<EditIcon />} />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="flex-start">
          <Typography variant="body2">
            {props.onSetWeight ? (
              <>{props.start_weight ? weightValueToDisplay(props.start_weight) : t('liveview.weightnotset')}</>
            ) : (
              '-'
            )}
          </Typography>
          <Stack sx={{ mt: '-10px', height: '10px' }}>
            {props.onSetWeight && (
              <TooltipIconButton onClick={props.onSetWeight} tooltip={t('logview.setweight')} icon={<EditIcon />} />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="flex-start">
          <Typography variant="body2">
            {props.onSetBean ? (
              <>
                {props.inventoryId || props.beanName ? (
                  <InventoryLink
                    log={{ inventory_id: props.inventoryId, bean_name: props.beanName } as unknown as Log}
                  />
                ) : (
                  t('liveview.beannotset')
                )}
              </>
            ) : (
              '-'
            )}
          </Typography>
          <Stack sx={{ mt: '-10px', height: '10px' }}>
            {props.onSetBean && (
              <TooltipIconButton onClick={props.onSetBean} tooltip={t('logview.setbean')} icon={<EditIcon />} />
            )}
          </Stack>
        </Stack>
        <Typography variant="body2">
          {props.profile ? (
            <ProfileLink
              machineSlug={props.machineSlug || ''}
              log={{ profile_id: props.profileId, profile_name: props.profile } as unknown as Log}
            />
          ) : (
            <>{'-'}</>
          )}
        </Typography>
        <Typography variant="body2">{props.status || '-'}</Typography>
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        {renderTempEl(t('logview.time'), msToTime(getLastValue(BasicDataParam.BT)?.msec || 0), true)}
        <Stack sx={{ mt: -1, mr: -1 }}>
          <TooltipIconButton
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchorEl(event.currentTarget)}
            icon={<DisplaySettingsIcon />}
            tooltip={t('liveview.configureseries')}
          />
        </Stack>
      </Stack>
      {[...props.seriesConfig]
        .sort((a, b) => enabledParams.indexOf(a.param) - enabledParams.indexOf(b.param))
        .filter((x) => enabledParams.indexOf(x.param) > -1)
        .map((x) => {
          const isTemp = x.seriesType === SeriesType.TEMPERATURE || x.seriesType === SeriesType.ROR
          const lastValue = getLastValue(x.param) || null
          const display = isTemp
            ? convertToTempScale(lastValue?.dataValue || 0, 1).toFixed(1)
            : `${lastValue?.dataValue}`
          const valueSuffix = getValueSuffix(x.seriesType) ? `(${getValueSuffix(x.seriesType)})` : ''
          const placeholder =
            x.seriesType === SeriesType.TEMPERATURE || x.seriesType === SeriesType.ROR ? '- - - . -' : '- -'
          const compare = props.compareSeriesConfig.find((compare) => compare.param === x.param)
          const compareBatch = compare?.seriesLabel.split(' ').find((w) => w.startsWith('#')) || 'Prev'
          const compareValue = (compare && getSeriesValue(x.param, lastValue?.msec || 0, compare)) || null
          const compareDisplay = isTemp
            ? convertToTempScale(compareValue?.dataValue || 0, 1).toFixed(1)
            : compareValue?.dataValue?.toFixed(1) || ''
          const compareDiff = (lastValue?.dataValue || 0) - (compareValue?.dataValue || 0)
          // paramName is not always RoR, but always some type of temperature difference
          const compareDiffDisplay = convertToTempScale(compareDiff || 0, 1, CalculatedDataParam.ROR)

          return (
            <React.Fragment key={x.param}>
              {renderTempEl(
                `${t(x.seriesLabel)} ${valueSuffix}`,
                lastValue ? display : placeholder,
                true,
                x.color,
                (compare && lastValue && renderCompareEl(compareBatch, compareDisplay, compareDiffDisplay)) || undefined
              )}
            </React.Fragment>
          )
        })}
      {enabledParams.indexOf(LAST_EVENT_SERIES) > -1 && renderTempEl(t('liveview.lastevent'), getLastEvent(), false)}
    </Stack>
  )
}

export default TempView
